import type { DynamicValue, PrimitiveValue, PrimitiveValueName } from "@skilitics/nautilus-core";
import {
  isNodeValueReference,
  isReferencedValue,
  isValueTombstone,
  valueReference,
  type ValueConfig,
} from "@skilitics/nautilus-core-designer-data";

import { defaultValueForConfig } from "./defaultValueForConfig.ts";
import { getExternalValueDescriptor } from "./externalValues.ts";
import type { GetNodeValue } from "./querying/getNodeValue.ts";
import type { ResolveDesignTimeValue } from "./resolveDesignTimeValue.ts";
import type { GetValueSubtypeDefinition } from "./subtype-definitions/getValueSubtypeDefinition.ts";
import { subtypeSatisfies, type ValueSubtypeRequirement } from "./valueSubtypes.ts";

interface CreateOptions {
  getNodeValue: GetNodeValue;
  resolveDesignTimeValue: ResolveDesignTimeValue;
}

interface RuntimeDynamicValueOptions extends CreateOptions {
  /** Only for the baked fallback: the target's own definition decides what "no value" is. */
  getValueSubtypeDefinition: GetValueSubtypeDefinition;
}

/** A judy resolvable emitted for a runtime-resolved condition value. */
export interface ConditionValueResolvable {
  type: PrimitiveValueName;
  resolveType: "nodeResultProperty" | "envProperty";
  name: string;
  nodeId?: number;
}

/**
 * Resolve a configured value for a condition (levels: design-time + runtime).
 * Prefers the runtime plane and falls back to a design-time literal; undefined
 * means the configured value can't be resolved (dangling/cyclic/type- or
 * subtype-mismatched) and the builder should abstain.
 *
 * A raw literal is checked against `type` only: it is the site's own control's
 * output and advertises no subtype of its own.
 */
export const resolveValueToConditionValueCreate = ({ getNodeValue, resolveDesignTimeValue }: CreateOptions) => {
  return (
    configuredValue: ValueConfig | undefined,
    type: PrimitiveValueName,
    /** Narrows what a reference may point at, as the consumer's picker does. */
    subtype?: ValueSubtypeRequirement,
  ): PrimitiveValue | ConditionValueResolvable | undefined => {
    if (isValueTombstone(configuredValue)) return undefined; // typed, but no value
    if (!isReferencedValue(configuredValue)) {
      return typeof configuredValue === type ? configuredValue : undefined;
    }
    const reference = valueReference(configuredValue);
    if (!isNodeValueReference(reference)) {
      const external = getExternalValueDescriptor(reference.property);
      if (external?.type !== type || !subtypeSatisfies(subtype, external.subtype)) return undefined;
      return { type, resolveType: "envProperty", name: reference.property };
    }
    const resolved = getNodeValue(reference);
    if (!resolved || resolved.descriptor.type !== type) return undefined;
    if (!subtypeSatisfies(subtype, resolved.descriptor.subtype)) return undefined;
    const { runtimePath } = resolved.descriptor;
    if (runtimePath) {
      return { type, resolveType: "nodeResultProperty", name: runtimePath, nodeId: reference.nodeId };
    }
    return resolveDesignTimeValue(configuredValue);
  };
};

export type ResolveValueToConditionValue = ReturnType<typeof resolveValueToConditionValueCreate>;

/**
 * Resolve a configured value to a runtime `Value` (levels: design-time + runtime) —
 * e.g. the "value to set to" in an effect payload. Prefers the runtime plane
 * (a `__dv` result-ref resolved against the target's result at read time) and
 * falls back to a design-time literal; undefined means the configured value
 * can't be resolved and the builder should abstain.
 *
 * A raw literal is checked against `requiredType` only: it is the site's own
 * control's output and advertises no subtype of its own.
 */
export const resolveValueToRuntimeDynamicValueCreate = ({
  getNodeValue,
  resolveDesignTimeValue,
  getValueSubtypeDefinition,
}: RuntimeDynamicValueOptions) => {
  return (
    configuredValue: ValueConfig | undefined,
    /** The type the consumer requires. Omit when it takes the value whatever its type. */
    requiredType?: PrimitiveValueName,
    /** Narrows what a reference may point at, as the consumer's picker does. */
    subtype?: ValueSubtypeRequirement,
  ): PrimitiveValue | DynamicValue | undefined => {
    if (isValueTombstone(configuredValue)) return undefined; // typed, but no value
    if (!isReferencedValue(configuredValue)) {
      if (configuredValue === undefined) return undefined;
      return !requiredType || typeof configuredValue === requiredType ? configuredValue : undefined;
    }
    const reference = valueReference(configuredValue);
    if (!isNodeValueReference(reference)) {
      const descriptor = getExternalValueDescriptor(reference.property);
      if (!descriptor || (requiredType && descriptor.type !== requiredType)) return undefined;
      if (!subtypeSatisfies(subtype, descriptor.subtype)) return undefined;
      return {
        __dv: {
          type: "ext",
          meta: { type: descriptor.type, fallback: descriptor.fallback },
          property: reference.property,
        },
      } as DynamicValue;
    }
    const resolved = getNodeValue(reference);
    if (!resolved || (requiredType && resolved.descriptor.type !== requiredType)) return undefined;
    if (!subtypeSatisfies(subtype, resolved.descriptor.subtype)) return undefined;
    const { type, subtype: targetSubtype, subtypeSpec, runtimePath, designTime } = resolved.descriptor;
    if (runtimePath) {
      // the baked fallback is a value of the target's own declaration, so it is
      // that declaration's default — a bounded number's floor, not zero
      const fallback =
        (designTime ? resolveDesignTimeValue(configuredValue) : undefined) ??
        getValueSubtypeDefinition({ type, subtype: targetSubtype }).defaultValue?.(targetSubtype, subtypeSpec) ??
        defaultValueForConfig(type);
      return {
        __dv: {
          type: "np",
          meta: { type, fallback },
          property: runtimePath,
          nodeId: reference.nodeId,
        },
      } as DynamicValue;
    }
    return resolveDesignTimeValue(configuredValue);
  };
};

export type ResolveValueToRuntimeDynamicValue = ReturnType<typeof resolveValueToRuntimeDynamicValueCreate>;
